import { type NewPartyDTO } from "#src/constants/party.ts";
import { Delete, Post, Put } from "#src/HttpClient.ts";
import { type InsuredBuild } from "#src/stores/step-underwriting.ts";
import { type RawEft } from "#src/types/Eft.ts";
import { type RawIndividual } from "#src/types/Individual.ts";
import { type RawInsuredOccupation } from "#src/types/InsuredOccupation.ts";

import { ROLE_TYPE, type RoleType } from "#src/types/Role.ts";
import { type DeepPartial } from "#src/types/TypeHelpers.ts";
import { createDateWithTimezone } from "#src/util/helpers.ts";
import { type Pinia } from "pinia";

const controller = "/strife/v1/individuals";

export async function createIndividual(pinia: Pinia, body: NewPartyDTO) {
  const { data } = await Post(pinia, {
    url: controller,
    includeEappData: true,
    body: { individual: body },
  });

  return data?.individual;
}

export async function updateIndividual(
  pinia: Pinia,
  id: number,
  body: any
): Promise<RawIndividual> {
  const { data } = await Put(pinia, {
    url: `${controller}/${id}`,
    body: { individual: body },
    includeEappData: true,
  });
  return data.individual;
}

export async function updateIndividualGuardian(
  pinia: Pinia,
  id: number,
  body: any
): Promise<RawIndividual> {
  const { data } = await Post(pinia, {
    url: `${controller}/${id}/guardian`,
    body: { guardian: body },
    includeEappData: true,
  });

  return data.individual;
}

export async function updateIndividualHomeAddress(
  pinia: Pinia,
  id: number,
  body: any
) {
  const { data } = await Put(pinia, {
    url: `${controller}/${id}/addresses/home`,
    body: { address: body },
    includeEappData: true,
  });

  return data.address;
}

export function deleteIndividual(pinia: Pinia, id: number) {
  return Delete(pinia, { url: `${controller}/${id}`, includeEappData: true });
}

export async function updateEft(
  pinia: Pinia,
  id: number,
  args: {
    routing_number: string;
    account_number: string;
    draft_day_of_month?: number;
  }
): Promise<RawEft> {
  const { data } = await Put(pinia, {
    url: `${controller}/${id}/eft`,
    body: {
      routing_number: args.routing_number,
      account_number: args.account_number,
      draft_day_of_month: args.draft_day_of_month,
    },
    includeEappData: true,
  });

  return data as RawEft;
}

export function scheduleExamTime(
  pinia: Pinia,
  id: number,
  args: { date: string }
) {
  return Post(pinia, {
    url: `${controller}/${id}/exam/schedule`,
    body: { date: args.date },
    includeEappData: true,
  });
}

export function skipExamRequirement(
  pinia: Pinia,
  id: number,
  args: { reason: string }
) {
  return Post(pinia, {
    url: `${controller}/${id}/exam/skip`,
    body: { reason: args.reason },
    includeEappData: true,
  });
}

export async function getExamAvailability(
  pinia: Pinia,
  id: number,
  args: { street_address: string; city: string; state: string; zip: string }
) {
  const { data } = await Post(pinia, {
    url: `${controller}/${id}/exam/availabilities`,
    includeEappData: true,
    body: {
      street_address: args.street_address,
      city: args.city,
      state: args.state,
      zip: args.zip,
    },
  });

  const availabilities = [];
  if (data?.availabilities) {
    for (const availability of data.availabilities) {
      const theDate = createDateWithTimezone(availability.starts_at);

      availabilities.push({
        originalTime: availability.starts_at,
        startsAt: new Date(theDate.getTimeInOriginalTimezone()),
      });
    }
  }
  return availabilities.toSorted(
    (a, b) => a.startsAt.getTime() - b.startsAt.getTime()
  );
}

export function updateIndividualDriversLicense(
  pinia: Pinia,
  id: number,
  drivers_license: any
) {
  return Put(pinia, {
    url: `${controller}/${id}/drivers_license`,
    body: { drivers_license },
    includeEappData: true,
  });
}

export function sendIntroMessage(
  pinia: Pinia,
  id: number,
  args: {
    send_client_intro_message?: boolean | null;
    first_name?: string | null;
    last_name?: string | null;
    phone_mobile?: string | null;
    email?: string | null;
  }
) {
  return Post(pinia, {
    url: `${controller}/${id}/send_intro_message`,
    includeEappData: true,
    body: {
      send_client_intro_message: args.send_client_intro_message,
      first_name: args.first_name,
      last_name: args.last_name,
      phone_mobile: args.phone_mobile,
      email: args.email,
    },
  });
}

export async function giveSpouseRole(pinia: Pinia, id: number) {
  const { data } = await Post(pinia, {
    url: `${controller}/${id}/spouse`,
    includeEappData: true,
  });

  const spousalRole = data.individual.application_roles.find(
    (role: { role: RoleType }) => role.role === ROLE_TYPE.SPOUSE
  );

  return spousalRole.id;
}

export async function giveJointInsuredRole(pinia: Pinia, id: number) {
  const { data } = await Post(pinia, {
    url: `${controller}/${id}/joint_insured`,
    includeEappData: true,
  });

  const jointInsuredRole = data.individual.application_roles.find(
    (role: { role: RoleType }) => role.role === ROLE_TYPE.JOINT_INSURED
  );

  return jointInsuredRole.id;
}

export function saveSmokingUsages(pinia: Pinia, id: number, statuses: any) {
  return Put(pinia, {
    url: `${controller}/${id}/smoking-statuses`,
    includeEappData: true,
    body: { statuses },
  });
}

export function deleteSmokingStatuses(pinia: Pinia, id: number) {
  return Delete(pinia, {
    url: `${controller}/${id}/smoking-statuses`,
    includeEappData: true,
  });
}

export interface OccupationUpdateDTO {
  occupation: {
    occupation: string;
    start_date: string;
  };
  employer: {
    name: string;
    address: {
      street_address: string;
      city: string;
      state: string;
      zip: string;
    };
  };
}

export async function occupationUpdate(
  pinia: Pinia,
  id: number,
  args: DeepPartial<OccupationUpdateDTO>
) {
  const { data } = await Put(pinia, {
    url: `${controller}/${id}/occupation`,
    includeEappData: true,
    body: {
      occupation: args.occupation,
      employer: args.employer,
      new_behavior: true,
    },
  });

  return data as RawInsuredOccupation;
}

export function occupationDelete(pinia: Pinia, id: number) {
  return Delete(pinia, {
    url: `${controller}/${id}/occupation`,
    includeEappData: true,
  });
}

export async function getInsuredEnvelopeId(pinia: Pinia, id: number) {
  const { data } = await Post(pinia, {
    url: `${controller}/${id}/signature`,
    includeEappData: true,
  });
  return data.url;
}

export async function getBuild(
  pinia: Pinia,
  id: number,
  args: { height: number; weight: number }
) {
  const { data } = await Post(pinia, {
    url: `${controller}/${id}/build`,
    includeEappData: true,
    body: { height: args.height, weight: args.weight },
  });

  const build: InsuredBuild = {
    minWeight: data?.build?.min_weight,
    maxWeight: data?.build?.max_weight,
    rating: data?.build?.rating,
    okayToProceed: data?.okay_to_proceed,
    valid: false,
    exists: false,
  };

  build.exists = !Object.values(build).every(v => v === null);

  if (build.okayToProceed) {
    build.valid = true;
  } else if (build.minWeight !== null && build.maxWeight !== null) {
    build.valid =
      args.weight >= build.minWeight && args.weight <= build.maxWeight;
  } else {
    build.valid = false;
  }
  return build;
}

export async function startHumanAPISession(pinia: Pinia, id: number) {
  const { data } = await Post(pinia, {
    url: `${controller}/${id}/human_session`,
    includeEappData: true,
  });
  return data?.session_token;
}

export function updateHumanAPIMedicalSources(
  pinia: Pinia,
  id: number,
  requested: any,
  connected: any
) {
  return Post(pinia, {
    url: `${controller}/${id}/medical_source`,
    includeEappData: true,
    body: { requested, connected },
  });
}
